import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { CheckCircle, Clock, CaretLeft, CaretRight, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function History() {
  const { activeCompanyId, activeCompany } = useCompany();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/history?company_id=${activeCompanyId}&year=${year}`);
    setData(data);
  }, [activeCompanyId, year]);

  useEffect(() => { load(); }, [load]);

  // Group items: templates as rows, months as columns
  const byTemplate = {};
  (data?.items || []).forEach((i) => {
    const key = i.template_id || i.name;
    if (!byTemplate[key]) byTemplate[key] = { name: i.name, vendor: i.vendor, months: {} };
    byTemplate[key].months[i.month] = i;
  });
  const rows = Object.values(byTemplate).sort((a, b) => a.name.localeCompare(b.name));

  const handleExport = async () => {
    if (!activeCompanyId) return;
    setExporting(true);
    try {
      const res = await api.get(`/export?company_id=${activeCompanyId}&year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const safe = (activeCompany?.name || "entity").replace(/[^\w\-]/g, "_");
      a.href = url;
      a.download = `${safe}_${year}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${year}`);
    } catch (e) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  return (
    <div data-testid="history-page" className="p-6 sm:p-8 max-w-[1400px]">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-tag mb-2">Year view</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
            {year} at a glance.
          </h1>
          <p className="text-[#525252] mt-3 max-w-xl">
            {activeCompany?.name}. One cell per template per month — submitted, pending, or not yet due.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-[#E5E5E5]">
            <button onClick={() => setYear(year - 1)} className="p-2 hover:bg-[#F5F5F5]" data-testid="history-year-prev">
              <CaretLeft size={16} weight="bold" />
            </button>
            <div className="px-4 font-heading font-medium text-lg" data-testid="history-year-label">{year}</div>
            <button onClick={() => setYear(year + 1)} className="p-2 hover:bg-[#F5F5F5]" data-testid="history-year-next">
              <CaretRight size={16} weight="bold" />
            </button>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-2 border border-[#E5E5E5] hover:bg-white hover:border-[#0A0A0A] transition-all text-sm flex items-center gap-2"
            data-testid="export-year-button"
          >
            <Archive size={16} weight="duotone" /> {exporting ? "Exporting…" : `Export ${year}`}
          </button>
        </div>
      </div>

      <div className="surface-card bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <th className="label-tag text-left px-4 py-3 sticky left-0 bg-[#FAFAFA] z-10 min-w-[220px]">Template</th>
              {MONTHS.map((m) => (
                <th key={m} className="label-tag text-center px-2 py-3 min-w-[60px]">{m}</th>
              ))}
              <th className="label-tag text-center px-3 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={14} className="p-10 text-center text-[#525252]">No activity for {year}.</td></tr>
            )}
            {rows.map((r) => {
              const submitted = Object.values(r.months).filter((i) => i.status === "submitted").length;
              return (
                <tr key={r.template_id || r.name} className="border-b border-[#E5E5E5] last:border-b-0 hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3 sticky left-0 bg-white z-10 border-r border-[#E5E5E5]">
                    <div className="font-medium text-sm">{r.name}</div>
                    {r.vendor && <div className="label-tag text-[10px]">{r.vendor}</div>}
                  </td>
                  {MONTHS.map((m, mi) => {
                    const key = `${year}-${String(mi + 1).padStart(2, "0")}`;
                    const cell = r.months[key];
                    const monthIdx = new Date().getMonth();
                    const isFuture = year > new Date().getFullYear() || (year === new Date().getFullYear() && mi > monthIdx);
                    return (
                      <td key={m} className="px-2 py-3 text-center">
                        {!cell || isFuture ? (
                          <span className="text-[#D4D4D4] text-xs">—</span>
                        ) : cell.status === "submitted" ? (
                          <CheckCircle size={18} weight="fill" className="text-[#10B981] inline" />
                        ) : (
                          <Clock size={18} weight="duotone" className="text-[#F59E0B] inline" />
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-center font-heading text-sm">
                    <span className={submitted === 12 ? "text-[#10B981]" : ""}>{submitted}</span>
                    <span className="text-[#525252]">/12</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-center gap-4 text-xs text-[#525252]">
        <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-[#10B981]" /> Submitted</span>
        <span className="flex items-center gap-1.5"><Clock size={14} weight="duotone" className="text-[#F59E0B]" /> Outstanding</span>
        <span className="flex items-center gap-1.5"><span className="text-[#D4D4D4] font-mono">—</span> Not yet due</span>
      </div>
    </div>
  );
}
